/*
 * Confidential and Proprietary.
 * Do not distribute without 1-800-Flowers.com, Inc. consent.
 * Copyright 1-800-Flowers.com, Inc. 2019. All rights reserved.
 */

import axios from 'axios';  // eslint-disable-line

import jwtDecode from 'jwt-decode';
import mbpUtil from 'mbp-api-util';

import payloadBuilder from './buildLoggingPayload';
import getCookieByName from './helpers/cookies';
import getLoggingServiceEndpoint from './getLoggingServiceEndpoint';

import ErrorModal from './components/ErrorModal/ErrorModal';
import localStorage from './helpers/localStorage';

const severities = ['DEBUG', 'INFO', 'WARN', 'ERROR', 'NONE'];

// const stackLimit = mbpUtil.getEnv('REACT_APP_MBP_LOGGER_STACKLIMIT') || 5;   // number of stack elements saved for error
const maxString = mbpUtil.getEnv('REACT_APP_MBP_LOGGER_MAXSTRING') || 256;
const loggingServiceEnabled = mbpUtil.getEnv('APP_LOGGING_ENABLED') || 'true';

// Threshold for console logging.
// When you want all messages logged to console, set to 'DEBUG' -- usual case for development
// When you want no messages logged to console, set to 'NONE' -- usual case for production
// When set to 'ERROR', only logs with 'ERROR' severity shown on console - no WARN, INFO or DEBUG
const consoleLoggingThreshold = mbpUtil.getEnv('REACT_APP_MBP_LOGGER_CONSOLE') || 'NONE';

//
// Internal functions
//
// (some of these are exported, but only for benefit of unit tests.  Do not use.)

// Returns true if given severity is at or under given threshold
// E.g. returns True if threshold is 'INFO' and severity is 'INFO', 'WARN' or 'ERROR'
//      returns False for everything if threshold is 'NONE' (or not a valid severity)
function severityWithinThreshold(severityParam, severityThresholdParam) {
    const severity = severityParam.toUpperCase();
    const thresholdIndex = severities.indexOf(severityThresholdParam.toUpperCase());
    return (thresholdIndex >= 0 && severities.indexOf(severity) >= thresholdIndex && severity !== 'NONE');
}

function capitalize(s) {
    return s.charAt(0).toUpperCase() + s.substr(1);
}

// if a[x] is present, set b[y] = a[x]   (if y omitted, set b[x] = a[x])
function assignIfPresent(sourceObj, sourceAttrib, targetObj, targetAttrib = null) {
    if (sourceObj[sourceAttrib]) {
        targetObj[targetAttrib || sourceAttrib] = sourceObj[sourceAttrib];  // eslint-disable-line no-param-reassign
    }
}

// Do any cleanup necessary for splunk keys.
function keyCleanup(k) {
    return k.trim().replace(/\s+/g, '_');   // replace any whitespace with an _
}

// Unpack parameter object into a "flat" object of key-value pairs
function unpackParamObj(paramObj) {
    const UNDEFINED_STRING = 'undefined';
    const NULL_STRING = 'null';
    const payload = {};

    Object.keys(paramObj).forEach((attrib) => {
        if (paramObj[attrib] === undefined) {
            payload[attrib] = UNDEFINED_STRING;
        } else if (paramObj[attrib] === null) {
            payload[attrib] = NULL_STRING;
        } else {
            switch (attrib) {
                case 'jsError':     // javascript Error object
                    ['name', 'message', 'description', 'fileName', 'lineNumber'].forEach((jsErrorAttrib) => {
                        assignIfPresent(paramObj.jsError, jsErrorAttrib, payload, `jsError${capitalize(jsErrorAttrib)}`);
                    });

                    // TODO figure out how to get stack into DD https://docs.datadoghq.com/logs/log_collection/?tab=host#attributes-and-tags
                    if (paramObj.jsError.stack) {
                        const { stack } = paramObj.jsError;
                        payload.errorMessage = paramObj.jsError.message || 'no error message';
                        payload.stack = stack.split(/[\r\n]+/).slice(1, 8).map(item => item.trim()).join(', ');
                    }

                    break;

                case 'reactErrorInfo':  // errorInfo object from React error boundary
                    if (paramObj.reactErrorInfo.componentStack) {
                        payload.errorMessage = paramObj.reactErrorInfo.message;
                        payload.stack = paramObj.reactErrorInfo.componentStack.split(/[\r\n]+/).slice(1, 8).map(item => item.trim()).join(', ');
                    }
                    break;

                case 'error':
                    if (paramObj.error.stack) {
                        const { stack, message } = paramObj.error;
                        payload.errorMessage = message;
                        payload.stack = stack.split(/[\r\n]+/).slice(1, 8).map(item => item.trim()).join(', ');
                    }
                    break;

                default:
                    if (typeof paramObj[attrib] === 'object') {
                        if (Array.isArray(paramObj[attrib])) {
                            paramObj[attrib].forEach((item, index) => {
                                const key = keyCleanup(`${attrib}${index}`);
                                if (item === null) {
                                    payload[key] = NULL_STRING;
                                } else if (item === undefined) {
                                    payload[key] = UNDEFINED_STRING;
                                } else {
                                    payload[key] = item.toString().trim().substr(0, maxString);
                                }
                            });
                        } else {
                            Object.keys(paramObj[attrib]).forEach((key) => {
                                const payloadKey = keyCleanup(`${attrib} ${key}`);
                                if (paramObj[attrib][key] === null) {
                                    payload[payloadKey] = NULL_STRING;
                                } else if (paramObj[attrib][key] === undefined) {
                                    payload[payloadKey] = UNDEFINED_STRING;
                                } else {
                                    payload[payloadKey] = paramObj[attrib][key].toString().trim().substr(0, maxString);
                                }
                            });
                        }
                    } else {
                        payload[attrib] = paramObj[attrib].toString().trim().substr(0, maxString);
                    }
            }
        }
    });

    return payload;
}

function mbpLog(keyValuePairObj, token = null) {
    const endpoint = getLoggingServiceEndpoint();

    // dont make api calls from dev env
    if (endpoint.includes('localhost')) {
        return;
    }

    const logEntryProps = { ...keyValuePairObj };

    if (loggingServiceEnabled && loggingServiceEnabled === 'true') {
        axios.post(getLoggingServiceEndpoint(), logEntryProps, { // TODO does this need to be called more than a single time per process?
            headers: {
                Authorization: `Bearer ${token}`,
            },
        }).catch((error) => {
            if (severityWithinThreshold('ERROR', consoleLoggingThreshold)) {
                console.error(JSON.stringify({ message: 'mbplogger: axios post failed', error }));
            }
        });
    }
}

// TODO rather than ignore in tests, convert to setting test env to silent severity
function sendToStdout(severity, payload) {
    if (typeof process !== 'undefined' && process.env && process.env.NODE_ENV === 'test') {
        return;
    }

    let stdoutFunction = console.error;
    if (severity === 'WARN') {
        stdoutFunction = console.warn;
    } else if (severity === 'INFO' || severity === 'DEBUG') {
        stdoutFunction = console.info;
    }

    if (typeof window === 'undefined') {
        stdoutFunction(JSON.stringify(payload));
    } else {
        stdoutFunction(payload);
    }
}


// TODO convert to class for injection and testing
function mbpLogWithSeverity(severity, paramObj, token = null) {
    const debugging = (global.mbp && global.mbp.ssrConfig && global.mbp.ssrConfig.debugging) ? global.mbp.ssrConfig.debugging : 'false';
    if (severityWithinThreshold(severity, consoleLoggingThreshold) || (debugging && debugging !== 'false')) {
        const JWT_PAYLOAD_KEY_1 = 'https://800-flowers.net';
        const JWT_PAYLOAD_KEY_2 = 'https://1800flowers.com/';
        const newParamObj = unpackParamObj(paramObj);

        newParamObj.severity = severity;
        newParamObj.time = (new Date()).getTime() / 1000.0;  // fractional seconds since epoch


        let jwt = token;
        let decodedToken = null;
        let jwtContactId = 'NO_CONTACT_ID';

        if (!jwt) {
            const raw = localStorage.get('userInfo');
            const data = JSON.parse(raw);
            if (data && data.token) {
                jwt = data.token;
                decodedToken = jwtDecode(jwt);
            }
        }

        if (decodedToken) {
            const jwtPayload = decodedToken[JWT_PAYLOAD_KEY_1] || decodedToken[JWT_PAYLOAD_KEY_2];
            jwtContactId = jwtPayload.contactId || 'NO_CONTACT_ID';
            newParamObj.contactId = jwtContactId;
        }

        if (debugging && debugging !== 'false') {
            newParamObj.uuid = debugging;
        }

        const payload = payloadBuilder.fromParamObj(newParamObj);

        sendToStdout?.(severity, payload);

        if (paramObj.UNIT_TEST) {
            return payload;
        }

        const gaCookie = getCookieByName('_ga');
        const shopperManager = getCookieByName('ShopperManagerEnterprise');
        
        return mbpLog({
            ...payload,
            contactId: jwtContactId,
            gaCookie,
            // TODO: this is a duplicate, see buildLogginPayload
            shopperManager,
        }, jwt);
    }

    return null;
}

//
// Exported functions
//

// Create log entry with given severity.
// If severity omitted or not valid, 'ERROR' is assumed.
// If severity is 'NONE', no logging is performed.
function logWithSeverity(paramObj, severity = 'ERROR', token = null) {
    const severityParam = severities.indexOf(severity.toUpperCase()) === -1 ? 'ERROR' : severity;
    if (severityParam !== 'NONE') {
        mbpLogWithSeverity(severityParam, paramObj, token);
    }
}

function logInfo(paramObj, token = null) {
    mbpLogWithSeverity('INFO', paramObj, token);
}

function logError(paramObj, token = null) {
    mbpLogWithSeverity('ERROR', paramObj, token);
}

function logWarning(paramObj, token = null) {
    mbpLogWithSeverity('WARN', paramObj, token);
}

function logDebug(paramObj, token = null) {
    mbpLogWithSeverity('DEBUG', paramObj, token);
}

export {
    logInfo,
    logError,
    logWarning,
    logDebug,
    logWithSeverity,

    // ErrorBoundary,
    ErrorModal,

    unpackParamObj,

    severityWithinThreshold,
    assignIfPresent,
    mbpLogWithSeverity,
};
